import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import AnimatedModal from '../ui/AnimatedModal';
import {
  BoltIcon,
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  XMarkIcon,
  CheckCircleIcon,
  SparklesIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowRightCircleIcon,
  TagIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

/**
 * AutomationsModal — recruiter-facing manager for screening automations.
 *
 * A rule reads: WHEN a candidate is fully evaluated → IF [conditions] → THEN [actions].
 * Two views: the rule list and the rule builder. The builder previews the candidates who currently
 * match, then lets the recruiter apply the rule to future candidates (a live automation), existing
 * ones (a one-time sweep), or both.
 *
 * Conditions offered are scoped to the stages the screening actually runs (resume / assessment /
 * interview). Rules act automatically when they match — there is no separate review/approve step.
 */

// ── operand / operator metadata (labels only; the server owns the enums) ──
const NUMERIC_OPERANDS = ['resume_score', 'assessment_score', 'interview_score', 'qualifying_score'];

const OPERAND_LABELS = {
  resume_score: 'Resume score',
  assessment_score: 'Assessment score (%)',
  interview_score: 'Interview score',
  critical_questions: 'Critical questions',
  deal_breaker: 'Deal-breaker concern',
  qualifying_score: 'Pre-screen call score',
  qualifying_interest: 'Candidate interest',
};

// Shown under the operand in the builder, because "Candidate interest" is the one operand whose
// meaning a recruiter cannot guess from its name — it is a yes/no gate taken from the call, not
// a number, and it is the condition the "call them, then move the keen ones on" rule is built on.
const OPERAND_HINTS = {
  qualifying_score: 'Weighted fit score from the qualifying call (0-100).',
  qualifying_interest:
    'Confirmed on the call that they want the role and will do the next step.',
};

const NUMERIC_OPERATORS = [
  { value: 'gte', label: 'is at least (≥)' },
  { value: 'lte', label: 'is at most (≤)' },
  { value: 'gt', label: 'is above (>)' },
  { value: 'lt', label: 'is below (<)' },
  { value: 'eq', label: 'equals (=)' },
];

// Gate operators depend on the operand.
const GATE_OPERATORS = {
  critical_questions: [
    { value: 'passed', label: 'were all answered correctly' },
    { value: 'failed', label: 'were failed (any wrong)' },
  ],
  deal_breaker: [
    { value: 'present', label: 'is present' },
    { value: 'absent', label: 'is absent' },
  ],
  qualifying_interest: [
    { value: 'passed', label: 'was confirmed on the call' },
    { value: 'failed', label: 'was declined on the call' },
  ],
};

const ACTION_LABELS = {
  reject: 'Move to Rejected',
  shortlist: 'Move to Selected',
  move_to_screening: 'Advance to another round',
  tag: 'Add a tag',
  notify: 'Send an email',
};

// Per-action icon for scannability in the list, builder, and activity feed.
const ACTION_ICONS = {
  reject: XCircleIcon,
  shortlist: CheckCircleIcon,
  move_to_screening: ArrowRightCircleIcon,
  tag: TagIcon,
  notify: EnvelopeIcon,
};

// Actions a recruiter can pick when building a rule. 'tag' and 'notify' are retired from the UI
// ('notify' never worked — it routed through the candidate-reminder job, which deletes itself
// once a candidate has finished, and automations only fire on finished candidates). Their entries
// stay in ACTION_LABELS/ACTION_ICONS only so any pre-existing rule still renders a readable label.
const SELECTABLE_ACTION_TYPES = ['reject', 'shortlist', 'move_to_screening'];
const RETIRED_ACTION_TYPES = ['tag', 'notify'];

function isNumericOperand(operand) {
  return NUMERIC_OPERANDS.includes(operand);
}

function operatorsFor(operand) {
  if (isNumericOperand(operand)) return NUMERIC_OPERATORS;
  return GATE_OPERATORS[operand] || [];
}

// A fresh condition for a given operand, with a sensible default operator/value for its type.
function makeCondition(operand) {
  const op = operatorsFor(operand)[0]?.value;
  return { operand, operator: op, value: isNumericOperand(operand) ? 60 : null };
}

function defaultAction() {
  return { type: 'reject', targetScreeningId: '' };
}

function hasRetiredAction(actions = []) {
  return actions.some((a) => RETIRED_ACTION_TYPES.includes(a.type));
}

// Human summary of a condition, e.g. "Interview score is at least 60".
function conditionSummary(c) {
  const operand = OPERAND_LABELS[c.operand] || c.operand;
  const opList = operatorsFor(c.operand);
  const op = opList.find((o) => o.value === c.operator)?.label || c.operator;
  return isNumericOperand(c.operand) ? `${operand} ${op} ${c.value}` : `${operand} ${op}`;
}

function actionSummary(a, destinations) {
  if (a.type === 'move_to_screening') {
    const dest = destinations.find((d) => String(d._id) === String(a.targetScreeningId));
    return `Advance to “${dest ? dest.name : 'another round'}”`;
  }
  if (a.type === 'tag') return `Tag: ${a.tagKey}${a.tagValue ? ` = ${a.tagValue}` : ''}`;
  return ACTION_LABELS[a.type] || a.type;
}

// Shared control styling so every select/input in the modal reads consistently.
const CONTROL =
  'px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-[#242124] transition-shadow ' +
  'focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF] outline-none';

export default function AutomationsModal({
  isOpen,
  onClose,
  jobId,
  screeningId: fixedScreeningId = null,
  onAppliedChanges,
}) {
  const [view, setView] = useState('list'); // 'list' | 'builder'
  const [loading, setLoading] = useState(false);
  const [automations, setAutomations] = useState([]);
  // Operands the active screening's stages can actually produce (server-computed). Drives which
  // conditions the builder offers.
  const [operands, setOperands] = useState([]);
  const [rounds, setRounds] = useState([]); // all screenings (rounds) of this job
  const [activeScreeningId, setActiveScreeningId] = useState(fixedScreeningId || '');
  const [editing, setEditing] = useState(null); // automation being edited, or null for new

  // "Advance" destinations = every other round of this job.
  const destinations = useMemo(
    () => rounds.filter((r) => String(r._id) !== String(activeScreeningId)),
    [rounds, activeScreeningId]
  );

  const loadRounds = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`/api/screenings/job/${jobId}`);
      const list = res.data?.screenings || [];
      setRounds(list);
      // Default the active round to the fixed one (detail-page shortcut) or the first round.
      setActiveScreeningId((prev) => prev || fixedScreeningId || list[0]?._id || '');
    } catch {
      setRounds([]);
    }
  }, [jobId, fixedScreeningId]);

  const loadAutomations = useCallback(async () => {
    if (!activeScreeningId) {
      setAutomations([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`/api/screenings/${activeScreeningId}/automations`);
      setAutomations(res.data?.automations || []);
      setOperands(res.data?.operands || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load automations');
    } finally {
      setLoading(false);
    }
  }, [activeScreeningId]);

  useEffect(() => {
    if (isOpen) {
      setView('list');
      loadRounds();
    }
  }, [isOpen, loadRounds]);

  useEffect(() => {
    if (isOpen) {
      loadAutomations();
    }
  }, [isOpen, activeScreeningId, loadAutomations]);

  const handleToggleEnabled = async (automation) => {
    try {
      await axios.patch(`/api/screenings/${activeScreeningId}/automations/${automation._id}`, {
        enabled: !automation.enabled,
      });
      loadAutomations();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    }
  };

  const handleDelete = async (automation) => {
    if (!window.confirm(`Delete automation “${automation.name}”? Its activity history is kept.`)) return;
    try {
      await axios.delete(`/api/screenings/${activeScreeningId}/automations/${automation._id}`);
      toast.success('Automation deleted');
      loadAutomations();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const openBuilder = (automation) => {
    setEditing(automation || null);
    setView('builder');
  };

  const activeRound = rounds.find((r) => String(r._id) === String(activeScreeningId));

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabelledBy="automations-title"
      backdropClassName="bg-black/50"
      contentClassName="w-full max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[90vh]"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-200 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#007BFF]/10">
            <BoltIcon className="h-5 w-5 text-[#007BFF]" />
          </span>
          <div className="min-w-0">
            <h2 id="automations-title" className="text-lg font-semibold text-[#242124] leading-tight">
              Automations
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Act on candidates automatically when a round finishes.
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Round selector — automations belong to a specific screening (round) of this job. */}
      {!fixedScreeningId && (
        <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-100 bg-[#F7F9FC] shrink-0">
          <label htmlFor="automation-round" className="text-xs font-medium text-gray-500 shrink-0">
            Round
          </label>
          <select
            id="automation-round"
            value={activeScreeningId}
            onChange={(e) => {
              setActiveScreeningId(e.target.value);
              setView('list');
            }}
            className={`${CONTROL} py-1.5 max-w-xs`}
          >
            {rounds.length === 0 && <option value="">No screenings yet</option>}
            {rounds.map((r) => (
              <option key={r._id} value={r._id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {!activeScreeningId ? (
          <div className="px-6 py-16 text-center">
            <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <BoltIcon className="h-6 w-6 text-gray-400" />
            </span>
            <p className="text-sm text-gray-500">
              Create a screening for this job to add automations.
            </p>
          </div>
        ) : (
          <>
            {view === 'list' && (
              <RulesList
                loading={loading}
                automations={automations}
                destinations={destinations}
                onNew={() => openBuilder(null)}
                onEdit={openBuilder}
                onToggle={handleToggleEnabled}
                onDelete={handleDelete}
              />
            )}
            {view === 'builder' && (
              <RuleBuilder
                key={editing?._id || 'new'}
                screeningId={activeScreeningId}
                roundName={activeRound?.name}
                destinations={destinations}
                availableOperands={operands}
                initial={editing}
                onEditConflict={(conflictId) => {
                  const target = automations.find(
                    (a) => String(a._id) === String(conflictId)
                  );
                  if (target) {
                    openBuilder(target);
                  }
                }}
                onCancel={() => setView('list')}
                onSaved={() => {
                  setView('list');
                  loadAutomations();
                  onAppliedChanges?.();
                }}
              />
            )}
          </>
        )}
      </div>
    </AnimatedModal>
  );
}

// ── Small presentational helpers ─────────────────────────────────────────────
function Chip({ children, tone = 'gray', icon: Icon }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-700 border-gray-200',
    blue: 'bg-[#007BFF]/10 text-[#007BFF] border-[#007BFF]/20',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${tones[tone]}`}
    >
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
      {children}
    </span>
  );
}

// A blue action chip carrying that action's icon (used in list + feed).
function ActionChip({ action, destinations }) {
  return (
    <Chip tone="blue" icon={ACTION_ICONS[action.type]}>
      {actionSummary(action, destinations)}
    </Chip>
  );
}

// Numbered step header for the builder's guided When → If → Then flow.
function StepHeader({ n, label, hint }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#007BFF] text-white text-xs font-semibold">
        {n}
      </span>
      <span className="text-sm font-semibold text-[#242124]">{label}</span>
      {hint && <span className="text-xs font-normal text-gray-400">{hint}</span>}
    </div>
  );
}

/**
 * Live plain-English rendering of the rule being built:
 * "When a candidate finishes Round 1 → if A and B → then X and Y".
 * Emphasised fragments read as a natural sentence so the recruiter can confirm intent at a glance.
 */
function RuleSentence({ roundName, conditions, actions, destinations }) {
  const em = 'font-semibold text-[#242124]';
  return (
    <p className="text-sm leading-relaxed text-gray-600">
      When a candidate finishes <span className={em}>{roundName || 'this round'}</span>
      {conditions?.length ? (
        <>
          , if{' '}
          {conditions.map((c, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="text-gray-400"> and </span>}
              <span className={em}>{conditionSummary(c)}</span>
            </React.Fragment>
          ))}
        </>
      ) : (
        <span className="text-gray-400"> (any candidate)</span>
      )}
      , then{' '}
      {actions?.length ? (
        actions.map((a, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-gray-400"> and </span>}
            <span className={em}>{actionSummary(a, destinations).toLowerCase()}</span>
          </React.Fragment>
        ))
      ) : (
        <span className="text-gray-400">nothing yet</span>
      )}
      .
    </p>
  );
}

// ── Rule list ──────────────────────────────────────────────────────────────
function RulesList({ loading, automations, destinations, onNew, onEdit, onToggle, onDelete }) {
  return (
    <div className="px-6 py-5">
      <div className="flex items-center justify-between gap-4 mb-4">
        <p className="text-sm text-gray-500">
          Rules run when a candidate finishes every step of this screening.
        </p>
        {automations.length > 0 && (
          <button
            onClick={onNew}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-white bg-[#007BFF] rounded-lg hover:bg-[#0069d9] shadow-sm transition-colors shrink-0"
          >
            <PlusIcon className="h-4 w-4" /> New automation
          </button>
        )}
      </div>

      {loading ? (
        <ul className="space-y-3">
          {[0, 1].map((i) => (
            <li key={i} className="h-[76px] rounded-xl border border-gray-200 bg-gray-50 animate-pulse" />
          ))}
        </ul>
      ) : automations.length === 0 ? (
        <div className="text-center py-14 px-6 border border-dashed border-gray-300 rounded-2xl bg-[#F7F9FC]">
          <span className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-[#007BFF]/10">
            <BoltIcon className="h-7 w-7 text-[#007BFF]" />
          </span>
          <p className="text-sm font-medium text-[#242124]">No automations yet</p>
          <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
            e.g. “If critical questions were failed → move to Rejected.” Rules act only after a
            candidate finishes this round.
          </p>
          <button
            onClick={onNew}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#007BFF] rounded-lg hover:bg-[#0069d9] shadow-sm transition-colors"
          >
            <PlusIcon className="h-4 w-4" /> Create your first automation
          </button>
        </div>
      ) : (
        <ul className="space-y-3">
          {automations.map((a) => (
            <li
              key={a._id}
              className={`group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-4 pl-5 transition-all hover:border-[#007BFF]/40 hover:shadow-sm ${
                a.enabled ? '' : 'opacity-70'
              }`}
            >
              {/* left accent */}
              <span
                className={`absolute inset-y-0 left-0 w-1 ${a.enabled ? 'bg-[#007BFF]' : 'bg-gray-300'}`}
                aria-hidden
              />
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-[#242124] truncate">{a.name}</span>
                    {!a.enabled && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                        Off
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      If
                    </span>
                    {a.conditions?.length ? (
                      a.conditions.map((c, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="text-[10px] font-semibold text-gray-400">AND</span>}
                          <Chip>{conditionSummary(c)}</Chip>
                        </React.Fragment>
                      ))
                    ) : (
                      <Chip>any candidate</Chip>
                    )}
                    <ArrowRightIcon className="h-3.5 w-3.5 text-gray-400 mx-0.5" />
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      Then
                    </span>
                    {a.actions?.map((ac, i) => (
                      <ActionChip key={i} action={ac} destinations={destinations} />
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {/* enable toggle */}
                  <button
                    onClick={() => onToggle(a)}
                    title={a.enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      a.enabled ? 'bg-[#007BFF]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        a.enabled ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                  <button
                    onClick={() => onEdit(a)}
                    title="Edit"
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(a)}
                    title="Delete"
                    className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Rule builder ─────────────────────────────────────────────────────────────
function RuleBuilder({
  screeningId,
  roundName,
  destinations,
  availableOperands,
  initial,
  onEditConflict,
  onCancel,
  onSaved,
}) {
  const isEdit = !!initial;

  // Operands offered in the dropdown = those the screening's stages produce, unioned with any
  // already used by the rule being edited (so an existing condition is never silently dropped).
  const operandOptions = useMemo(() => {
    const fromScreening = availableOperands || [];
    const used = (initial?.conditions || []).map((c) => c.operand);
    return Array.from(new Set([...fromScreening, ...used]));
  }, [availableOperands, initial]);
  const hasNoOperands = operandOptions.length === 0;

  const [name, setName] = useState(initial?.name || '');
  const [conditions, setConditions] = useState(
    initial?.conditions?.length
      ? initial.conditions.map((c) => ({ ...c }))
      : operandOptions[0]
        ? [makeCondition(operandOptions[0])]
        : []
  );
  const [actions, setActions] = useState(
    initial?.actions?.length
      ? initial.actions.map((a) => ({ ...defaultAction(), ...a }))
      : [defaultAction()]
  );
  const isRetiredRule = hasRetiredAction(actions);
  // Apply the rule to candidates evaluated from now on (a live, forward automation). Defaults on;
  // for an existing rule it reflects whether it's currently enabled.
  const [applyToFuture, setApplyToFuture] = useState(isEdit ? initial.enabled !== false : true);
  // One-time sweep over already-evaluated candidates (create only).
  const [applyToExisting, setApplyToExisting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [showMatches, setShowMatches] = useState(false); // reveal the matched-candidate list
  const [saving, setSaving] = useState(false);
  // Set when the server rejects the save because this rule overlaps an existing enabled rule.
  const [conflict, setConflict] = useState(null); // { id, name } | null

  // Live preview count (debounced) — "this would match N of M evaluated candidates".
  useEffect(() => {
    if (hasNoOperands || isRetiredRule) {
      setPreview(null);
      setPreviewing(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewing(true);
      try {
        const res = await axios.post(`/api/screenings/${screeningId}/automations/preview`, {
          conditions,
        });
        if (!cancelled) setPreview(res.data);
      } catch {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [conditions, hasNoOperands, isRetiredRule, screeningId]);

  // Editing the conditions may resolve the overlap, so clear a stale conflict warning.
  useEffect(() => {
    setConflict(null);
  }, [conditions]);

  const updateCondition = (idx, patch) => {
    setConditions((prev) =>
      prev.map((c, i) => {
        if (i !== idx) return c;
        const next = { ...c, ...patch };
        // When the operand type changes, reset operator/value to something valid.
        if (patch.operand) {
          const ops = operatorsFor(patch.operand);
          next.operator = ops[0]?.value;
          next.value = isNumericOperand(patch.operand) ? 60 : null;
        }
        return next;
      })
    );
  };

  const updateAction = (idx, patch) => {
    setActions((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };

  const handleSave = async () => {
    if (hasNoOperands) {
      toast.error('This round has no scoring stages available for automation');
      return;
    }
    if (isRetiredRule) {
      toast.error('This automation uses a retired action and cannot be edited');
      return;
    }
    if (!name.trim()) {
      toast.error('Give the automation a name');
      return;
    }
    setSaving(true);
    setConflict(null);
    // Rules always act automatically now (the suggest/review flow is gone). "Apply to future"
    // just toggles whether the rule is enabled to run on candidates evaluated from now on.
    const payload = {
      name: name.trim(),
      autonomy: 'auto',
      enabled: applyToFuture,
      conditions,
      actions,
    };
    try {
      if (isEdit) {
        await axios.patch(`/api/screenings/${screeningId}/automations/${initial._id}`, payload);
        toast.success('Automation updated');
      } else {
        const res = await axios.post(`/api/screenings/${screeningId}/automations`, {
          ...payload,
          backfill: applyToExisting ? 'apply' : 'none',
        });
        const bf = res.data?.backfill;
        if (bf && applyToExisting) {
          toast.success(`Created — applied to ${bf.applied} existing candidate(s)`);
        } else {
          toast.success('Automation created');
        }
      }
      onSaved();
    } catch (err) {
      // 409 = overlap with an existing enabled rule; surface it inline so the recruiter can
      // jump straight to editing that rule instead of creating a shadowed duplicate.
      if (err.response?.status === 409 && err.response?.data?.conflictWith) {
        setConflict(err.response.data.conflictWith);
      }
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (isRetiredRule) {
    return (
      <div className="flex flex-col">
        <div className="px-6 py-5">
          <button
            onClick={onCancel}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors mb-5"
          >
            <ArrowLeftIcon className="h-4 w-4" /> Back to rules
          </button>
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
            <div className="flex items-start gap-2.5">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-900">Retired automation action</p>
                <p className="text-xs text-amber-800 mt-1">
                  This rule uses a retired tag or email action. It can stay visible for history, but
                  it cannot be edited from the current builder. Use the rule list to disable or
                  delete it.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {actions.map((a, i) => (
                    <ActionChip key={i} action={a} destinations={destinations} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (hasNoOperands) {
    return (
      <div className="flex flex-col">
        <div className="px-6 py-5">
          <button
            onClick={onCancel}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors mb-5"
          >
            <ArrowLeftIcon className="h-4 w-4" /> Back to rules
          </button>
          <div className="text-center py-12 px-6 border border-dashed border-gray-300 rounded-2xl bg-[#F7F9FC]">
            <span className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
              <BoltIcon className="h-7 w-7 text-gray-400" />
            </span>
            <p className="text-sm font-medium text-[#242124]">
              No scoring stages available for automation
            </p>
            <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
              Add resume scoring, an assessment, or an interview to this round before creating
              automation rules.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="px-6 py-5">
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors mb-5"
        >
          <ArrowLeftIcon className="h-4 w-4" /> Back to rules
        </button>

        {/* Name */}
        <label className="block text-sm font-semibold text-[#242124] mb-1.5">
          Automation name
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Reject on critical-question failure"
          className={`${CONTROL} w-full`}
        />

        {/* Step 1 — WHEN (informational trigger) */}
        <div className="mt-6">
          <StepHeader n={1} label="When" hint="· the trigger" />
          <div className="mt-2 flex items-start gap-2.5 rounded-xl border border-gray-200 bg-[#F7F9FC] px-4 py-3">
            <BoltIcon className="h-4 w-4 text-[#007BFF] mt-0.5 shrink-0" />
            <p className="text-sm text-gray-600">
              A candidate completes every step of
              {roundName ? (
                <>
                  {' '}
                  <span className="font-medium text-[#242124]">{roundName}</span>
                </>
              ) : (
                ' this round'
              )}
              .
            </p>
          </div>
        </div>

        {/* Step 2 — IF (conditions) */}
        <div className="mt-5">
          <StepHeader n={2} label="If" hint="· all must be true" />
          <div className="mt-2 rounded-xl border border-gray-200 p-3 space-y-2">
            {conditions.map((c, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && (
                  <div className="flex items-center gap-2 py-0.5">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 bg-gray-100 rounded px-2 py-0.5">
                      And
                    </span>
                    <span className="h-px flex-1 bg-gray-100" />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <select
                    value={c.operand}
                    onChange={(e) => updateCondition(idx, { operand: e.target.value })}
                    className={`${CONTROL} py-1.5 flex-1 min-w-0`}
                  >
                    {operandOptions.map((op) => (
                      <option key={op} value={op}>
                        {OPERAND_LABELS[op] || op}
                      </option>
                    ))}
                  </select>
                  <select
                    value={c.operator}
                    onChange={(e) => updateCondition(idx, { operator: e.target.value })}
                    className={`${CONTROL} py-1.5 flex-1 min-w-0`}
                  >
                    {operatorsFor(c.operand).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {isNumericOperand(c.operand) && (
                    <input
                      type="number"
                      value={c.value ?? ''}
                      onChange={(e) => updateCondition(idx, { value: e.target.value })}
                      className={`${CONTROL} py-1.5 w-20 shrink-0`}
                    />
                  )}
                  {conditions.length > 1 && (
                    <button
                      onClick={() => setConditions((prev) => prev.filter((_, i) => i !== idx))}
                      title="Remove condition"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {OPERAND_HINTS[c.operand] && (
                  <p className="pl-1 text-[11px] leading-snug text-gray-500">
                    {OPERAND_HINTS[c.operand]}
                  </p>
                )}
              </React.Fragment>
            ))}
            <button
              onClick={() => setConditions((prev) => [...prev, makeCondition(operandOptions[0])])}
              className="mt-1 w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 py-2 text-xs font-medium text-[#007BFF] hover:border-[#007BFF]/50 hover:bg-[#007BFF]/5 transition-colors"
            >
              <PlusIcon className="h-3.5 w-3.5" /> Add condition
            </button>
          </div>
        </div>

        {/* Step 3 — THEN (actions) */}
        <div className="mt-5">
          <StepHeader n={3} label="Then" hint="· what happens" />
          <div className="mt-2 space-y-3">
            {actions.map((a, idx) => (
              <div key={idx} className="rounded-xl border border-gray-200 p-3 bg-white">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#007BFF]/10 text-[#007BFF]">
                    {React.createElement(ACTION_ICONS[a.type] || BoltIcon, { className: 'h-4 w-4' })}
                  </span>
                  <select
                    value={a.type}
                    onChange={(e) => updateAction(idx, { type: e.target.value })}
                    className={`${CONTROL} py-1.5 flex-1 min-w-0`}
                  >
                    {SELECTABLE_ACTION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {ACTION_LABELS[t]}
                      </option>
                    ))}
                  </select>
                  {actions.length > 1 && (
                    <button
                      onClick={() => setActions((prev) => prev.filter((_, i) => i !== idx))}
                      title="Remove action"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {a.type === 'move_to_screening' && (
                  <select
                    value={a.targetScreeningId || ''}
                    onChange={(e) => updateAction(idx, { targetScreeningId: e.target.value })}
                    className={`${CONTROL} py-1.5 mt-2 w-full`}
                  >
                    <option value="">Select destination round…</option>
                    {destinations.map((d) => (
                      <option key={d._id} value={d._id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                )}

              </div>
            ))}
          </div>
          <button
            onClick={() => setActions((prev) => [...prev, defaultAction()])}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 py-2 text-xs font-medium text-[#007BFF] hover:border-[#007BFF]/50 hover:bg-[#007BFF]/5 transition-colors"
          >
            <PlusIcon className="h-3.5 w-3.5" /> Add action
          </button>
        </div>

        {/* Rule summary — plain-English sentence + live match count */}
        <div className="mt-6 rounded-xl border border-[#007BFF]/20 bg-[#007BFF]/5 p-4">
          <div className="flex items-center gap-1.5 mb-2">
            <SparklesIcon className="h-4 w-4 text-[#007BFF]" />
            <span className="text-[11px] font-bold uppercase tracking-wide text-[#007BFF]">
              Rule summary
            </span>
          </div>
          <RuleSentence
            roundName={roundName}
            conditions={conditions}
            actions={actions}
            destinations={destinations}
          />
          <div className="mt-3 pt-3 border-t border-[#007BFF]/15 flex items-center justify-between gap-3 text-sm text-gray-600">
            <span>
              {previewing ? (
                <span className="text-gray-400">Checking how many candidates match…</span>
              ) : preview ? (
                <>
                  Matches <span className="font-semibold text-[#007BFF]">{preview.matched}</span> of{' '}
                  {preview.evaluated} fully-evaluated candidate(s) right now.
                </>
              ) : (
                <span className="text-gray-400">Live match preview unavailable.</span>
              )}
            </span>
            {preview?.matched > 0 && (
              <button
                type="button"
                onClick={() => setShowMatches((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#007BFF]/30 bg-white px-3 py-1.5 text-xs font-semibold text-[#007BFF] hover:bg-[#007BFF]/10 transition-colors shrink-0"
              >
                {showMatches ? 'Hide' : 'Preview'} matching candidates
              </button>
            )}
          </div>

          {/* The actual condition-satisfied candidate set. */}
          {showMatches && preview?.matchedCandidates?.length > 0 && (
            <ul className="mt-3 space-y-1.5 max-h-56 overflow-y-auto rounded-lg border border-[#007BFF]/15 bg-white p-2">
              {preview.matchedCandidates.map((c, i) => (
                <li key={c.email || i} className="flex items-center gap-2.5 px-2 py-1.5">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                    {(c.name || c.email || '?').trim().charAt(0).toUpperCase() || '?'}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#242124] truncate">
                      {c.name || c.email}
                    </p>
                    {c.name && c.email && (
                      <p className="text-xs text-gray-400 truncate">{c.email}</p>
                    )}
                  </div>
                </li>
              ))}
              {preview.matched > preview.matchedCandidates.length && (
                <li className="px-2 py-1 text-xs text-gray-400">
                  …and {preview.matched - preview.matchedCandidates.length} more
                </li>
              )}
            </ul>
          )}
        </div>

        {/* Apply — how this rule reaches candidates. */}
        <div className="mt-6">
          <label className="block text-sm font-semibold text-[#242124] mb-2">Apply this rule</label>
          <div className="space-y-2">
            <label className="flex items-start gap-2.5 rounded-xl border border-gray-200 p-3 cursor-pointer hover:border-gray-300 transition-colors">
              <input
                type="checkbox"
                checked={applyToFuture}
                onChange={(e) => setApplyToFuture(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]/30"
              />
              <span>
                <span className="block text-sm font-medium text-[#242124]">
                  Apply to future candidates
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Acts automatically the moment a candidate finishes this round.
                </span>
              </span>
            </label>
            {!isEdit && (
              <label className="flex items-start gap-2.5 rounded-xl border border-gray-200 p-3 cursor-pointer hover:border-gray-300 transition-colors">
                <input
                  type="checkbox"
                  checked={applyToExisting}
                  onChange={(e) => setApplyToExisting(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]/30"
                />
                <span>
                  <span className="block text-sm font-medium text-[#242124]">
                    Apply to existing candidates now
                    {preview?.matched > 0 && (
                      <span className="text-gray-500 font-normal"> ({preview.matched})</span>
                    )}
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Runs once over candidates who are already fully evaluated.
                  </span>
                </span>
              </label>
            )}
          </div>
        </div>

        {/* Overlap guardrail — this rule targets candidates an existing enabled rule already acts on. */}
        {conflict && (
          <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-4">
            <div className="flex items-start gap-2.5">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-900">
                  Overlaps with “{conflict.name}”
                </p>
                <p className="text-xs text-amber-800 mt-1">
                  Both rules would act on some of the same candidates, and only one can win. Edit
                  “{conflict.name}” instead, or narrow this rule’s conditions so they don’t overlap.
                </p>
                {onEditConflict && (
                  <button
                    type="button"
                    onClick={() => onEditConflict(conflict.id)}
                    className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors"
                  >
                    <PencilSquareIcon className="h-3.5 w-3.5" /> Edit “{conflict.name}” instead
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save / cancel — sticky footer */}
      <div className="sticky bottom-0 flex justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 text-sm font-semibold text-white bg-[#007BFF] rounded-lg hover:bg-[#0069d9] shadow-sm disabled:opacity-60 transition-colors"
        >
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create automation'}
        </button>
      </div>
    </div>
  );
}
